import {Color} from '@benchling/nucleus-ui';
import {Loader, Popover, ScrollArea, TextInput, Tooltip, UnstyledButton, createStyles} from '@mantine/core';
import type {MantineTheme} from '@mantine/core';
import {useDebouncedValue} from '@mantine/hooks';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import FolderDAO from 'file-browser/FolderDAO';
import {fetchUserTypeahead, type UserTypeaheadItem} from 'omnibox/OmniboxDAO';
import {
  buildFolderTreeNodes,
  type FolderTreeNode,
  searchFolderTree,
  visibleFolderTreeRows,
} from 'omnibox/omniboxFolderTree';
import type {OmniboxFilter} from 'omnibox/useTypeaheadData';
import BenchlingIcon, {type BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as logging from 'util/Logging';

// The three type-toggle pills are mutually exclusive; "user" (Authored By) and "project"
// (Folder) each open a dropdown.
export type OmniboxFilterTabId = 'entry' | 'entity' | 'file' | 'user' | 'project';

const TYPE_TABS: ReadonlyArray<{id: 'entry' | 'entity' | 'file'; label: string; icon: BenchlingIconName}> = [
  {id: 'entry', label: 'Entry', icon: 'file-lines-outline'},
  {id: 'entity', label: 'Entity', icon: 'square-list-outline'},
  {id: 'file', label: 'File', icon: 'paperclip'},
];

// Share of the filter row's width the Folder dropdown spans, centered beneath the omnibox
// so nested folder names have room after per-level indentation.
const FOLDER_DROPDOWN_WIDTH_RATIO = 0.8;
// Used until the filter row has been measured (e.g. when no anchor is provided).
const FOLDER_DROPDOWN_FALLBACK_WIDTH = 320;
// Per-level indentation in the folder tree, wide enough that each level clearly nests under its parent.
const FOLDER_INDENT_PX = 30;
// Height of the folder dropdown's scrolling list.
const FOLDER_LIST_HEIGHT = 420;
// Rows rendered at once; a broad search on a large tenant could otherwise match thousands of folders.
const FOLDER_MAX_RENDERED_ROWS = 500;

const useStyles = createStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box' as const,
    // Fixed-height row inside the omnibox's flex column: never shrink when results fill the modal to
    // its max-height, or the pills row gets squeezed upward as results land.
    flexShrink: 0,
    height: 30,
    padding: '0px 16px 8px',
    gap: 8,
    backgroundColor: theme.white,
    // Side borders only, per Figma (Frame 1000002826).
    borderLeft: `1px solid ${Color.GRAY_400}`,
    borderRight: `1px solid ${Color.GRAY_400}`,
  },
  // Group 38: the pill group, left-aligned within the row.
  pillGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    height: 22,
  },
  iconWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 18,
    height: 18,
    borderRadius: 27,
    backgroundColor: Color.GRAY_100,
    color: Color.GRAY_600,
  },
  dropdown: {
    padding: 0,
  },
  // Sized to the widest author row so names and handles show in full, with a floor that keeps the
  // loading and empty states from collapsing and a cap past which long rows truncate.
  authorDropdown: {
    minWidth: 260,
    maxWidth: 400,
  },
  folderDropdown: {
    padding: 0,
    borderRadius: theme.radius.mdplus,
    overflow: 'hidden',
  },
  folderSearchWrapper: {
    padding: `${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.sm}`,
  },
  // Fixed height so the dropdown holds a steady shape across states; scrolls on overflow.
  folderScrollArea: {
    height: FOLDER_LIST_HEIGHT,
  },
  folderOptionList: {
    padding: `${theme.spacing.xs} ${theme.spacing.sm} ${theme.spacing.sm}`,
  },
  folderOption: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    minHeight: 40,
    padding: '6px 8px',
    borderRadius: theme.radius.smplus,
    fontSize: theme.fontSizes.lg,
    color: Color.GRAY_900,
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
    '&[aria-selected="true"]': {
      backgroundColor: theme.colors.blue[0],
    },
  },
  // A search result's location in the tree, right-aligned after its name.
  folderPath: {
    flexShrink: 0,
    maxWidth: '50%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: theme.fontSizes.sm,
    color: Color.GRAY_600,
  },
  // Rounded tile holding a project/folder's type icon; a selected row shows a check in its place.
  folderIconTile: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    borderRadius: theme.radius.smplus,
    backgroundColor: Color.GRAY_100,
    color: Color.GRAY_600,
    fontSize: theme.fontSizes.md,
  },
  folderCheck: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    color: Color.GREEN_500,
    fontSize: theme.fontSizes.xl,
  },
  searchWrapper: {
    padding: 8,
    borderBottom: `1px solid ${Color.GRAY_150}`,
  },
  // ScrollArea.Autosize wraps its content in a flex column that would otherwise grow to fit the
  // widest row, pushing long names, handles, and the scrollbar past the dropdown's max width.
  autosizeScrollArea: {
    '& > div': {
      minWidth: 0,
    },
  },
  // The scroll viewport lays its content out as a table, which also sizes to the widest row; a
  // block fills the viewport width so long rows truncate instead.
  scrollViewport: {
    '& > div': {
      display: 'block !important',
    },
  },
  optionList: {
    padding: 4,
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '6px 8px',
    borderRadius: theme.radius.sm,
    fontSize: 13,
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  // A tree row pairs an expand/collapse toggle with the selectable option; indentation is applied
  // per-row by nesting depth.
  treeRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  treeSelect: {
    flex: 1,
    minWidth: 0,
    width: 'auto',
  },
  expandToggle: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: theme.radius.sm,
    color: Color.GRAY_600,
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
  },
  // Aligns rows with no children with those that have an expand toggle.
  expandSpacer: {
    flexShrink: 0,
    width: 22,
  },
  treeChevron: {
    fontSize: 12,
    color: Color.GRAY_600,
  },
  optionName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  optionHandle: {
    color: Color.GRAY_600,
    fontSize: 11,
    maxWidth: '50%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  optionCheck: {
    flexShrink: 0,
    color: theme.colors.blue[6],
    width: 14,
    fontSize: 12,
  },
  // Icon inside a pill trigger (type/author/project) and its dropdown chevron.
  pillIcon: {
    fontSize: 12,
  },
  pillChevron: {
    fontSize: 8,
  },
  emptyOption: {
    padding: '8px 12px',
    fontSize: 12,
    color: Color.GRAY_600,
    textAlign: 'center' as const,
  },
  loadingOption: {
    display: 'flex',
    justifyContent: 'center',
    padding: '8px 12px',
  },
  // Fill and center within the fixed-height dropdown so it holds a steady shape across states.
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: FOLDER_LIST_HEIGHT - 12,
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: FOLDER_LIST_HEIGHT - 12,
    padding: '8px 12px',
    fontSize: 12,
    color: Color.GRAY_600,
    textAlign: 'center' as const,
  },
}));

// Mantine's `width` prop converts pixels to rem against a 16px base, which renders narrower under the
// app's smaller root font size; a class keeps the measured width in pixels. !important is needed to
// override the inline `width: max-content` Mantine sets when no `width` prop is given.
//
// The dropdown is anchored to the pill and shifted sideways by `marginLeft` to center it under the
// filter row. The shift is a margin rather than a floating-ui offset because the popover keeps applying
// the offset it opened with: its auto-update is not resubscribed when the offset changes, so a
// resize that changes the dropdown's width would leave it off center.
const useFolderDropdownStyles = createStyles((_theme, {width, shift}: {width: number; shift: number}) => ({
  dropdown: {
    width: `${width}px !important`,
    marginLeft: shift,
  },
}));

/** Leading slot on a project/folder row: the type icon in a tile, or a check once selected. */
function FolderOptionIcon({isFolder, selected}: {isFolder: boolean; selected: boolean}): JSX.Element {
  const {classes} = useStyles();
  return selected ? (
    <span className={classes.folderCheck} role="img" aria-label="Selected">
      <BenchlingIcon icon="check" aria-hidden="true" />
    </span>
  ) : (
    <span className={classes.folderIconTile}>
      <BenchlingIcon icon={isFolder ? 'folder' : 'source'} aria-hidden="true" />
    </span>
  );
}

// Shared visual treatment for every filter pill (type toggles + the author trigger).
function pillSx(isActive: boolean) {
  return (theme: MantineTheme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    width: 'fit-content',
    height: 22,
    whiteSpace: 'nowrap' as const,
    borderRadius: 10,
    border: `1px solid ${Color.GRAY_300}`,
    padding: '2px 9px 2px 2px',
    // Active pills use the selected label treatment from Figma (600 / 12px, blue[7] = BLUE_450);
    // inactive pills stay 400 / 10px in GRAY_900.
    fontSize: isActive ? 12 : 10,
    fontWeight: isActive ? 600 : 400,
    lineHeight: '16px',
    letterSpacing: 0,
    color: isActive ? theme.colors.blue[7] : Color.GRAY_900,
    backgroundColor: isActive ? theme.colors.blue[0] : 'transparent',
    transition: 'all 100ms ease',
    '&:hover': {
      backgroundColor: isActive ? theme.colors.blue[0] : theme.colors.gray[0],
    },
  });
}

/** Option label that shows the full name in a tooltip on hover, since long names are cut off. */
function OptionName({name}: {name: string}): JSX.Element {
  const {classes} = useStyles();
  return (
    // The omnibox Modal is zIndex 1055 and the filter dropdown 1056; the tooltip must sit above both.
    <Tooltip label={name} withinPortal={true} zIndex={1057} openDelay={300}>
      <span className={classes.optionName}>{name}</span>
    </Tooltip>
  );
}

interface AuthoredByPillProps {
  isActive: boolean;
  selectedAuthors: ReadonlyArray<OmniboxFilter>;
  onToggleAuthor: (author: OmniboxFilter) => void;
}

function AuthoredByPill({isActive, selectedAuthors, onToggleAuthor}: AuthoredByPillProps): JSX.Element {
  const {classes, cx} = useStyles();
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);

  const selectedAuthorIds = useMemo(() => new Set(selectedAuthors.map((a) => a.id)), [selectedAuthors]);

  // Author filtering is single-select, so the pill reflects the one chosen author inline instead of
  // rendering a separate filter chip. Falls back to the plain "Authored By" label when none is set.
  const pillLabel =
    selectedAuthors.length === 1
      ? `Authored by: ${selectedAuthors[0].isSelf ? 'You' : selectedAuthors[0].name}`
      : 'Authored By';

  // populate options when a user starts typing in the search input, debounced to avoid excessive API calls
  const [search, setSearch] = useState('');
  const [debouncedSearchValue] = useDebouncedValue(search, 300);
  const [options, setOptions] = useState<Array<UserTypeaheadItem>>([]);

  useEffect(() => {
    if (!opened) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchUserTypeahead(debouncedSearchValue.trim())
      .then((users: Array<UserTypeaheadItem>) => {
        if (!cancelled) {
          setOptions(users);
        }
      })
      .catch((e) => {
        // Drop stale results on failure so a prior query's users can't be shown (or selected)
        // under the current search text.
        if (!cancelled) {
          setOptions([]);
        }
        // eslint-disable-next-line no-console
        console.error('Failed to fetch users in typeahead', e);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearchValue, opened]);

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      withinPortal={true}
      zIndex={1056} // The omnibox Modal renders at z-Index 1055; the portaled dropdown must sit above it (matching OmniboxFooter's Popover).
      shadow="md"
      radius="md"
    >
      <Popover.Target>
        <UnstyledButton
          aria-pressed={isActive}
          aria-haspopup="listbox"
          aria-expanded={opened}
          onClick={() => {
            setOpened((o) => !o);
          }}
          sx={pillSx(isActive)}
        >
          <span className={classes.iconWrapper}>
            <BenchlingIcon icon="image-polaroid-user" className={classes.pillIcon} aria-hidden="true" />
          </span>
          {pillLabel}
          <BenchlingIcon icon="chevron-down" className={classes.pillChevron} aria-hidden="true" />
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown className={cx(classes.dropdown, classes.authorDropdown)}>
        <div className={classes.searchWrapper}>
          <TextInput
            size="xs"
            placeholder="Filter by author..."
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
            }}
            autoFocus={true}
            aria-label="Filter by author"
          />
        </div>
        <ScrollArea.Autosize
          mah={240}
          type="auto"
          className={classes.autosizeScrollArea}
          classNames={{viewport: classes.scrollViewport}}
        >
          <div className={classes.optionList} role="listbox" aria-label="Authors">
            {loading ? (
              <div className={classes.loadingOption} role="status" aria-label="Loading authors">
                <Loader size="xs" />
              </div>
            ) : options.length === 0 ? (
              <div className={classes.emptyOption}>No matching people</div>
            ) : (
              options.map((author) => {
                const selected = selectedAuthorIds.has(author.apiId);
                return (
                  <UnstyledButton
                    key={author.apiId}
                    role="option"
                    aria-selected={selected}
                    className={classes.option}
                    onClick={() => {
                      onToggleAuthor({
                        kind: 'user',
                        id: author.apiId,
                        name: author.name,
                        handle: author.handle,
                        isSelf: author.isSelf,
                      });
                    }}
                  >
                    <span className={classes.optionName}>{author.isSelf ? 'You' : author.name}</span>
                    {author.handle && <span className={classes.optionHandle}>@{author.handle}</span>}
                    {selected && (
                      <BenchlingIcon icon="check" className={classes.optionCheck} aria-hidden="true" />
                    )}
                  </UnstyledButton>
                );
              })
            )}
          </div>
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
}

interface ProjectFolderPillProps {
  isActive: boolean;
  // The filter row the dropdown is sized and centered against, rather than the pill itself.
  anchorRef: React.RefObject<HTMLDivElement>;
  selectedProjectFolders: ReadonlyArray<OmniboxFilter>;
  onToggleProjectFolder: (item: OmniboxFilter) => void;
}

function ProjectFolderPill({
  isActive,
  anchorRef,
  selectedProjectFolders,
  onToggleProjectFolder,
}: ProjectFolderPillProps): JSX.Element {
  const {classes, cx} = useStyles();
  const [opened, setOpened] = useState(false);
  const pillRef = useRef<HTMLButtonElement>(null);
  const [dropdownLayout, setDropdownLayout] = useState({width: FOLDER_DROPDOWN_FALLBACK_WIDTH, shift: 0});
  const {classes: dropdownClasses} = useFolderDropdownStyles(dropdownLayout);

  // The dropdown is anchored to the pill, so center it under the filter row by shifting it by the
  // distance between the pill's left edge and the centered dropdown's.
  const measureDropdown = useCallback((): void => {
    const anchor = anchorRef.current;
    const pill = pillRef.current;
    if (!anchor || !pill) {
      return;
    }
    const anchorRect = anchor.getBoundingClientRect();
    // An unmeasured row would collapse the dropdown; keep the last layout instead.
    if (anchorRect.width === 0) {
      return;
    }
    const width = Math.round(anchorRect.width * FOLDER_DROPDOWN_WIDTH_RATIO);
    const left = anchorRect.left + (anchorRect.width - width) / 2;
    setDropdownLayout({width, shift: Math.round(left - pill.getBoundingClientRect().left)});
  }, [anchorRef]);

  // The omnibox width is viewport-relative, so re-measure on resize while the dropdown is open.
  useEffect(() => {
    if (!opened) {
      return undefined;
    }
    window.addEventListener('resize', measureDropdown);
    return () => {
      window.removeEventListener('resize', measureDropdown);
    };
  }, [opened, measureDropdown]);

  const selectedProjectFolderIds = useMemo(
    () => new Set(selectedProjectFolders.map((f) => f.id)),
    [selectedProjectFolders]
  );

  const selectedProjectFolder = selectedProjectFolders.length === 1 ? selectedProjectFolders[0] : null;
  const pillLabel = selectedProjectFolder
    ? `${selectedProjectFolder.kind === 'folder' ? 'In folder' : 'In project'}: ${selectedProjectFolder.name}`
    : 'Folder';

  // Every readable folder loads once through the session-wide folder cache the sidepanel folder
  // filter also uses, so the tree, search, and expand all work client-side without further requests.
  // Fetched on mount rather than on open so the dropdown is usually ready by the time it opens.
  const [folderNodes, setFolderNodes] = useState<Array<FolderTreeNode> | null>(null);
  useEffect(() => {
    let cancelled = false;
    Promise.resolve(FolderDAO.fetchAll({level: 'read'}))
      .then((folders) => {
        if (!cancelled) {
          setFolderNodes(buildFolderTreeNodes(folders ?? []));
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setFolderNodes([]);
        }
        logging.error('Failed to fetch folders for the omnibox folder filter', {extra: {error: e}});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const [search, setSearch] = useState('');
  const [debouncedSearchValue] = useDebouncedValue(search, 200);
  // Browsing shows the tree, starting collapsed; a search flattens its matches into one level, each
  // labeled with where it sits in the tree.
  const searchQuery = debouncedSearchValue.trim();
  const searching = searchQuery !== '';
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(new Set());

  const toggleExpand = (treeId: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(treeId)) {
        next.delete(treeId);
      } else {
        next.add(treeId);
      }
      return next;
    });
  };

  const treeRows = useMemo(
    () => (searching ? [] : visibleFolderTreeRows(folderNodes ?? [], expandedIds)),
    [searching, folderNodes, expandedIds]
  );
  const searchResults = useMemo(
    () => (searching ? searchFolderTree(folderNodes ?? [], searchQuery) : []),
    [searching, folderNodes, searchQuery]
  );
  const rowCount = searching ? searchResults.length : treeRows.length;

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      offset={14}
      withinPortal={true}
      // The omnibox Modal renders at zIndex 1055; the portaled dropdown must sit above it
      // (matching OmniboxFooter's Popover) or it renders behind the modal and is invisible.
      zIndex={1056}
      shadow="md"
      radius="md"
    >
      <Popover.Target>
        <UnstyledButton
          ref={pillRef}
          aria-pressed={isActive}
          aria-haspopup="listbox"
          aria-expanded={opened}
          onClick={() => {
            if (!opened) {
              measureDropdown();
            }
            setOpened((o) => !o);
          }}
          sx={pillSx(isActive)}
        >
          <span className={classes.iconWrapper}>
            <BenchlingIcon icon="folder" className={classes.pillIcon} aria-hidden="true" />
          </span>
          {pillLabel}
          <BenchlingIcon icon="chevron-down" className={classes.pillChevron} aria-hidden="true" />
        </UnstyledButton>
      </Popover.Target>
      {/* Fixed width; Mantine otherwise sizes the dropdown to its content, which changes between states. */}
      <Popover.Dropdown className={cx(classes.folderDropdown, dropdownClasses.dropdown)}>
        <div className={classes.folderSearchWrapper}>
          <TextInput
            size="sm"
            placeholder="Filter by project or folder..."
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
            }}
            autoFocus={true}
            aria-label="Filter by project or folder"
          />
        </div>
        <ScrollArea type="auto" className={classes.folderScrollArea}>
          <div className={classes.folderOptionList} role="listbox" aria-label="Projects and folders">
            {folderNodes == null ? (
              <div className={classes.loadingState} role="status" aria-label="Loading projects and folders">
                <Loader size="xs" aria-hidden="true" />
              </div>
            ) : rowCount === 0 ? (
              <div className={classes.emptyState}>No matching projects or folders</div>
            ) : (
              <>
                {searching
                  ? searchResults.slice(0, FOLDER_MAX_RENDERED_ROWS).map(({node, path}) => {
                      const {item, filter} = node;
                      const selected = selectedProjectFolderIds.has(filter.id);
                      return (
                        <UnstyledButton
                          key={item.id}
                          role="option"
                          aria-selected={selected}
                          className={classes.folderOption}
                          onClick={() => {
                            onToggleProjectFolder(filter);
                          }}
                        >
                          <FolderOptionIcon isFolder={filter.kind === 'folder'} selected={selected} />
                          <OptionName name={item.name} />
                          {path && <span className={classes.folderPath}>{path}</span>}
                        </UnstyledButton>
                      );
                    })
                  : treeRows.slice(0, FOLDER_MAX_RENDERED_ROWS).map(({node, depth, hasChildren}) => {
                      const {item, filter} = node;
                      const selected = selectedProjectFolderIds.has(filter.id);
                      const expanded = expandedIds.has(item.id);
                      return (
                        <div
                          key={item.id}
                          className={classes.treeRow}
                          style={{paddingLeft: depth * FOLDER_INDENT_PX}}
                        >
                          {hasChildren ? (
                            <UnstyledButton
                              className={classes.expandToggle}
                              aria-label={expanded ? 'Collapse' : 'Expand'}
                              aria-expanded={expanded}
                              onClick={() => {
                                toggleExpand(item.id);
                              }}
                            >
                              <BenchlingIcon
                                icon={expanded ? 'chevron-down' : 'chevron-right'}
                                className={classes.treeChevron}
                                aria-hidden="true"
                              />
                            </UnstyledButton>
                          ) : (
                            <span className={classes.expandSpacer} />
                          )}
                          <UnstyledButton
                            role="option"
                            aria-selected={selected}
                            className={cx(classes.folderOption, classes.treeSelect)}
                            onClick={() => {
                              onToggleProjectFolder(filter);
                            }}
                          >
                            <FolderOptionIcon isFolder={filter.kind === 'folder'} selected={selected} />
                            <OptionName name={item.name} />
                          </UnstyledButton>
                        </div>
                      );
                    })}
                {rowCount > FOLDER_MAX_RENDERED_ROWS && (
                  <div className={classes.emptyOption}>
                    Showing the first {FOLDER_MAX_RENDERED_ROWS}. Type to narrow the list.
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </Popover.Dropdown>
    </Popover>
  );
}

interface OmniboxFilterTabsProps {
  activeTabIds: ReadonlySet<OmniboxFilterTabId>;
  onTabClick: (id: 'entry' | 'entity' | 'file') => void;
  selectedAuthors: ReadonlyArray<OmniboxFilter>;
  onToggleAuthor: (author: OmniboxFilter) => void;
  selectedProjectFolders: ReadonlyArray<OmniboxFilter>;
  onToggleProjectFolder: (item: OmniboxFilter) => void;
  className?: string;
}

export default function OmniboxFilterTabs({
  activeTabIds,
  onTabClick,
  selectedAuthors,
  onToggleAuthor,
  selectedProjectFolders,
  onToggleProjectFolder,
  className,
}: OmniboxFilterTabsProps): JSX.Element {
  const {classes} = useStyles();
  const rootRef = useRef<HTMLDivElement>(null);
  return (
    <div ref={rootRef} className={className ?? classes.root}>
      <div role="group" aria-label="Filter search results by type" className={classes.pillGroup}>
        {TYPE_TABS.map((tab) => {
          const isActive = activeTabIds.has(tab.id);
          return (
            <UnstyledButton
              key={tab.id}
              aria-pressed={isActive}
              onClick={() => {
                onTabClick(tab.id);
              }}
              sx={pillSx(isActive)}
            >
              <span className={classes.iconWrapper}>
                <BenchlingIcon icon={tab.icon} className={classes.pillIcon} aria-hidden="true" />
              </span>
              {tab.label}
            </UnstyledButton>
          );
        })}
        <ProjectFolderPill
          anchorRef={rootRef}
          isActive={activeTabIds.has('project')}
          selectedProjectFolders={selectedProjectFolders}
          onToggleProjectFolder={onToggleProjectFolder}
        />
        <AuthoredByPill
          isActive={activeTabIds.has('user')}
          selectedAuthors={selectedAuthors}
          onToggleAuthor={onToggleAuthor}
        />
      </div>
    </div>
  );
}
